import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Link,
  Alert,
  styled,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useForgotPasswordMutation } from "Services/auth";

const MainContainer = styled(Box)({
  display: "flex",
  minHeight: "100vh",
  backgroundColor: "#f5f5f5",
  alignItems: "center",
  justifyContent: "center",
});

const Card = styled(Paper)({
  padding: "48px",
  width: "100%",
  maxWidth: "440px",
  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  borderRadius: "12px",
});

const SubmitButton = styled(Button)({
  background: "linear-gradient(90deg, #2A7BFF 0%, #3F8CFF 100%)",
  color: "white",
  padding: "12px",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "15px",
  "&:hover": {
    background: "linear-gradient(90deg, #1D63E8 0%, #2A7BFF 100%)",
  },
});

const StyledTextField = styled(TextField)({
  marginBottom: "20px",
  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
});

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const [email, setEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    try {
      await forgotPassword({ email }).unwrap();
      setSent(true);
      setTimeout(() => navigate("/reset-password", { state: { email } }), 1500);
    } catch (err: any) {
      setErrorMsg(err?.data?.message || "Failed to send reset code");
    }
  };

  return (
    <MainContainer>
      <Card>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Forgot Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your email and we'll send you a reset code.
        </Typography>

        {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
        {sent && <Alert severity="success" sx={{ mb: 2 }}>Reset code sent! Redirecting…</Alert>}

        <form onSubmit={handleSubmit}>
          <StyledTextField
            fullWidth
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <SubmitButton fullWidth type="submit" disabled={isLoading || sent}>
            {isLoading ? "Sending…" : "Send Reset Code"}
          </SubmitButton>
        </form>

        <Box textAlign="center" mt={3}>
          <Link component="button" variant="body2" underline="hover" onClick={() => navigate("/login")}>
            Back to Sign In
          </Link>
        </Box>
      </Card>
    </MainContainer>
  );
};

export default ForgotPassword;
